import { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const NEIGHBOR_COLORS = [
  'rgba(220, 53, 69, 0.8)',
  'rgba(25, 135, 84, 0.8)',
  'rgba(255, 153, 0, 0.85)',
  'rgba(111, 66, 193, 0.8)',
  'rgba(0, 188, 212, 0.8)',
  'rgba(214, 51, 132, 0.8)',
  'rgba(108, 117, 125, 0.8)',
  'rgba(253, 126, 20, 0.8)',
  'rgba(32, 201, 151, 0.8)',
  'rgba(13, 202, 240, 0.8)',
];

function formatWeek(iso) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export default function LeaguePositionCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate, modelDatesParam } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (modelDatesParam) params.set('modelDates', modelDatesParam);
    apiFetch(`/league-position?${params}`, token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, modelDatesParam]);

  const chartData = useMemo(() => {
    if (!data || !data.traces || data.traces.length === 0) return null;

    const highlightedTraces = data.traces.filter((t) => t.highlighted);

    let colorIdx = 0;
    const datasets = highlightedTraces.map((trace) => {
      if (trace.selected) {
        return {
          label: teamAcronym(trace.team),
          data: trace.positions,
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 3.5,
          pointRadius: 4,
          pointBackgroundColor: 'rgba(13, 110, 253, 1)',
          tension: 0.3,
          spanGaps: true,
          order: 0,
        };
      }
      const color = NEIGHBOR_COLORS[colorIdx++ % NEIGHBOR_COLORS.length];
      return {
        label: teamAcronym(trace.team),
        data: trace.positions,
        borderColor: color,
        borderWidth: 2,
        pointRadius: 2,
        pointBackgroundColor: color,
        tension: 0.3,
        spanGaps: true,
        order: 1,
      };
    });

    return {
      labels: data.weeks.map(formatWeek),
      datasets,
    };
  }, [data]);

  const chartOptions = useMemo(() => {
    if (!data) return {};

    const highlightedTraces = data.traces.filter((t) => t.highlighted);
    const allPositions = highlightedTraces.flatMap((t) => t.positions.filter((p) => p != null));
    let yMin = 1;
    let yMax = data.totalTeams || 20;
    if (allPositions.length) {
      const minPos = Math.min(...allPositions);
      const maxPos = Math.max(...allPositions);
      const padding = Math.max(1, Math.ceil((maxPos - minPos) * 0.15));
      yMin = Math.max(1, minPos - padding);
      yMax = Math.min(data.totalTeams || 20, maxPos + padding);
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 12,
            font: { size: 11 },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'Week', font: { size: 11 } },
          ticks: { font: { size: 10 } },
        },
        y: {
          reverse: true,
          title: { display: true, text: 'Position', font: { size: 11 } },
          min: yMin,
          max: yMax,
          ticks: {
            font: { size: 10 },
            stepSize: 1,
            callback: (v) => Number.isInteger(v) ? v : '',
          },
        },
      },
    };
  }, [data]);

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>League Position</strong>
        <span className="ms-auto text-muted small">Last 6 weeks</span>
      </div>
      <div className="card-body">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && (!data || data.traces.length === 0) && (
          <div className="text-center py-4 text-muted small">No data available</div>
        )}
        {!loading && !error && chartData && (
          <div style={{ height: '220px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </div>
  );
}
